import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from './config/api';
import Landing from './components/Landing';
import SignIn from './components/SignIn';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import Dashboard from './components/Dashboard';
import SearchJob from './components/SearchJob';
import SearchStaff from './components/SearchStaff';
import CreateJobOffer from './components/CreateJobOffer';
import MyJobOffers from './components/MyJobOffers';
import AddAvailability from './components/AddAvailability';
import MyAvailability from './components/MyAvailability';
import MyEngagements from './components/MyEngagements';
import Profile from './components/Profile';
import Settings from './components/Settings';
import CheckoutSuccess from './components/CheckoutSuccess';
import ProtectedRoute from './components/ProtectedRoute';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminUsers from './components/admin/AdminUsers';
import AdminJobOffers from './components/admin/AdminJobOffers';
import AdminAvailability from './components/admin/AdminAvailability';
import AdminReports from './components/admin/AdminReports';
import AdminNotifications from './components/admin/AdminNotifications';
import AdminTokens from './components/admin/AdminTokens';
import AdminPackages from './components/admin/AdminPackages';
import NotificationModalsHost from './components/NotificationModalsHost';
import { ConfirmProvider } from './context/ConfirmContext';
import { ToastProvider } from './context/ToastContext';
import './styles/App.css';
import './i18n';

const AppContent: React.FC = () => {
    const { i18n } = useTranslation();

    useEffect(() => {
        const loadUserLanguage = async () => {
            const token = localStorage.getItem('authToken');
            
            if (token) {
                try {
                    const response = await fetch(getApiUrl('users/profile'), {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });

                    if (response.ok) {
                        const userData = await response.json();
                        if (userData.preferredLanguage) {
                            i18n.changeLanguage(userData.preferredLanguage);
                        }
                    }
                } catch (error) {
                    console.error('Error loading user language:', error);
                }
            }
        };

        loadUserLanguage();
    }, [i18n]);

    return (
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/search-job" element={<SearchJob />} />
            <Route path="/search-staff" element={<SearchStaff />} />
            <Route
                path="/create-job-offer"
                element={
                    <ProtectedRoute>
                        <CreateJobOffer />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/my-job-offers"
                element={
                    <ProtectedRoute>
                        <MyJobOffers />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/add-availability"
                element={
                    <ProtectedRoute>
                        <AddAvailability />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/my-availability"
                element={
                    <ProtectedRoute>
                        <MyAvailability />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/my-engagements"
                element={
                    <ProtectedRoute>
                        <MyEngagements />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/profile"
                element={
                    <ProtectedRoute>
                        <Profile />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings"
                element={
                    <ProtectedRoute>
                        <Settings />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/checkout-success"
                element={
                    <ProtectedRoute>
                        <CheckoutSuccess />
                    </ProtectedRoute>
                }
            />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/tokens" element={<AdminTokens />} />
            <Route path="/admin/job-offers" element={<AdminJobOffers />} />
            <Route path="/admin/availability" element={<AdminAvailability />} />
            <Route path="/admin/reports" element={<AdminReports />} />
            <Route path="/admin/notifications" element={<AdminNotifications />} />
            <Route path="/admin/packages" element={<AdminPackages />} />
        </Routes>
    );
};

const App: React.FC = () => {
    return (
        <Router>
            <ToastProvider>
                <ConfirmProvider>
                    <AppContent />
                    <NotificationModalsHost />
                </ConfirmProvider>
            </ToastProvider>
        </Router>
    );
};

export default App;